import type { FC } from 'react';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNamedContainers } from '@alfa-bank/site-named-container/utils';

import { StoreProvider } from '../wrapper/store-provider';
import { defaultTitleDesktop, formSteps, modalSteps } from '../config';
import { getPropValue } from '../helpers/helpers';
import {
    setAppAllowSendingAfter,
    selectActiveFormStep,
    selectActiveModalStep,
    setAppAnalyticsWebView,
    setAppShowProgressBar,
} from '../store';
import type { TInvestSmevDesktop } from '../models';
import { Route } from '../components/route';
import { ShadowBoxDesktop } from '../components/shadow-box/desktop';
import { EsiaLoginStepDesktop } from '../view/esia-login/desktop';
import { EsiaAnalysisStepDesktop } from '../view/esia-analysis/desktop';
import { DeliveryPreparingDcDeliveryStepDesktop } from '../view/delivery-preparing-dc-delivery/desktop';
import { DeliveryBookCourierDeliveryDesktop } from '../view/delivery-book-courier-delivery/desktop';
import { ErrorAuthorizationToEsiaDesktop } from '../view/error-authorization-to-esia/desktop';
import { ErrorAuthorizationToCourierCallDesktop } from '../view/error-authorization-to-courier-call/desktop';
import { ErrorOverlimitDesktop } from '../view/error-overlimit/desktop';
import { ErrorOverlimitCountdownDesktop } from '../view/error-overlimit-countdown/desktop';
import { DeliveryFinalStepDesktop } from '../view/delivery-final-step/desktop';
import { DeliveryFinalStepWithErrorDesktop } from '../view/delivery-final-step-with-error/desktop';
import { DeliverySystemErrorStepDesktop } from '../view/delivery-system-error/desktop';
import { DeliveryBookOfficeDeliveryDesktop } from '../view/delivery-book-office-delivery/desktop';
import { ErrorDeliveryNotPreparedDesktop } from '../view/error-delivery-not-prepared/desktop';
import { PassportAdditionStepDesktop } from '../view/passport-addition/desktop';
import { AddressAdditionStepDesktop } from '../view/address-addition/desktop';
import { defaultErrorSettings } from '../view/error/config/error';
import { defaultErrorRefusalOpenAccount } from '../view/error/config/error-refusal-open-account';
import { defaultErrorInviteToOffice } from '../view/error/config/error-invite-to-office';
import { defaultErrorPassportNotValid } from '../view/error/config/error-passport-not-valid';
import { defaultErrorAuthorizationToCancelledSettingsDesktop } from '../view/error/config/error-authorization-to-cancelled';
import { defaultErrorUnderageSettings } from '../view/error/config/error-underage';
import { defaultErrorEsiaInvalidResponseSettings } from '../view/error/config/error-esia-invalid-response';
import { defaultValuesErrorDeliveryNotPrepared } from '../view/error-delivery-not-prepared/config/default-values';
import { defaultBroCancelledCourierDeliveryApplicationApprovedDesktop } from '../view/delivery-final-step-with-error/config/bro-cancelled-courier-delivery-application-approved';
import { defaultBroCancelledOfficeDeliveryApplicationApprovedDesktop } from '../view/delivery-final-step-with-error/config/bro-cancelled-office-delivery-application-approved';
import { defaultErrorInviteToOfficeR04R05 } from '../view/delivery-final-step-with-error/config/error-invite-to-office-r04-r05';
import { defaultWebViewAnalytics } from '../config/default-webview-analytics';
import { analyticsMetaSmev } from '../config/meta-steps-analytics';
import { proxySendEvent, sendEventSmev } from '../helpers/proxy-send-event';
import { defaultDeliveryFinalStepSettingsDesktop } from '../view/delivery-final-step/config/default-values';
import { defaultValuesCourierDeliveryApplicationApprovedNoInnDesktop } from '../view/delivery-final-step/config/default-values-courier-delivery-application-approved-no-inn';
import { AwaitingClientInnStepDesktop } from '../view/awaiting-client-inn/desktop';
import { AwaitingFnsInnIdentificationStepDesktop } from '../view/awaiting-fns-inn-identification/desktop';

/** Нужно сделать манифест по всем шагам вместо этого огромного файла, пример манифеста будет ниже
 * Это нужно чтобы все было в одном месте и не валялось по всему проекту */
// const stepsManifest = {
//     [formSteps.init]: {
//         analytics: {},
//         view: Component,
//         route: formSteps.init,
//     },
// };

// TODO нужно везде отключить прокидывание аналитики через пропсы
/**
 * Идеи на будущее если рефачить или при следующих доработках
 * ---------------------------------------
 * Нужно объединить все финальные шаги delivery которые не уникальные в один
 * также нужно объединить все настройки для этих шагов в один
 * ---------------------------------------
 * Добавить для каждого шага формы свой dataTestId
 * Добавить для каждого поля формы свой dataTestId
 * Добавить для последних экранов свой dataTestId
 * Сделать dataTestId привязанным к названию шага
 * ---------------------------------------
 * Подумать чтобы сделать аналагичную обработку ошибок и конфиги
 * packages/cc/cc-personal-offers/src/api/endpoints/code.ts
 * ---------------------------------------
 * Все текстовки ошибок должны быть вынесены в отдельные константы
 * ---------------------------------------
 * Можно удалить этот компонент так как теперь там можно передать кастомную валидацию
 * packages/invest/invest-form-smev/src/components/phone
 * ---------------------------------------
 * Можно удалить компоненты:
 * - components/custom-button/
 * - components/invest-button/
 * ---------------------------------------
 * вьюхи delivery-cancelled / delivery-final-step / delivery-system-error
 * можно объединить в одну
 * ---------------------------------------
 */
export const InvestSmevDesktop: FC<TInvestSmevDesktop> = ({
    Block,
    ChangingAddress,
    EsiaDataAdditionStep,
    DocumentSignSmsVerification,
    DocumentsSignInformationClientStep,
    DocumentsSignInformationStep,
    Error,
    InitStep,
    SmsVerification,
    accountType,
    analyticsWebView = defaultWebViewAnalytics,
    applicationAcceptedEsiaClientStepSettings,
    applicationAcceptedIsDcNoDeliveryStepSettings,
    applicationAcceptedNoDcNoDeliveryStepSettings,
    applicationAcceptedNoStatusDcDeliveryStepSettings,
    awaitingClientInnStepSettings,
    awaitingFnsInnIdentificationStepSettings,
    changingAddressSettings,
    children,
    className,
    courierDeliveryApplicationApprovedStepSettings,
    courierDeliveryApplicationApprovedNoInnStepSettings,
    dataTestId = 'smev',
    deliveryBookCourierDeliveryStepSettings,
    deliveryBookCourierDeliveryNoInnStepSettings,
    deliveryBookOfficeDeliveryStepPublicProps,
    deliveryBookOfficeDeliveryNoInnStepPublicProps,
    deliveryPreparingDcDeliveryStepSettings,
    deliveryPreparingDcDeliveryNoInnStepSettings,
    deliverySystemErrorStepSettings,
    broCancelledCourierDeliveryApplicationApprovedSettings,
    broCancelledOfficeDeliveryApplicationApprovedSettings,
    errorInviteToOfficeR04R05Settings,
    disableButtonForSeconds,
    esiaDataAdditionStepSettings,
    passportAdditionStepSettings,
    addressAdditionStepSettings,
    documentSignInformationClientStepSettings,
    documentSignInformationStepSettings,
    errorAuthorizationToCancelledStepSettings,
    errorAuthorizationToCourierCallStepSettings,
    errorAuthorizationToEsiaStepSettings,
    errorDeliveryNotPreparedStepSettings,
    errorInviteToOfficeStepSettings,
    errorOverlimitStepSettings,
    errorOverlimitCountdownStepSettings,
    errorPassportNotValidStepSettings,
    errorRefusalOpenAccountStepSettings,
    errorStepSettings,
    errorUnderageStepSettings,
    esiaAnalysisStepSettings,
    esiaLoginStepSettings,
    initStepSettings,
    officeDeliveryApplicationApprovedStepSettings,
    officeDeliveryApplicationApprovedNoInnStepSettings,
    title = defaultTitleDesktop,
    teenagerSettings,
    showProgressBar = true,
    loadingIconSrc,
}) => {
    const currentFormStep = useSelector(selectActiveFormStep);
    const currentModalStep = useSelector(selectActiveModalStep);
    const {
        deliveryCommonFinalStepSlot,
        deliveryCourierAlfaCardSlot,
        deliveryOfficeAlfaCardSlot,
        deliverySystemErrorSlot,
        sopdContentSlot,
    } = useNamedContainers(children);

    const dispatch = useDispatch();

    useEffect(() => {
        sendEventSmev(analyticsMetaSmev.onFormView);
        proxySendEvent({
            webViewEvent: analyticsWebView?.initLoad,
        });
        dispatch(setAppAnalyticsWebView(analyticsWebView));
        dispatch(setAppShowProgressBar(showProgressBar));
    }, [analyticsWebView, dispatch, showProgressBar]);

    useEffect(() => {
        if (disableButtonForSeconds) {
            dispatch(setAppAllowSendingAfter(Date.now() + disableButtonForSeconds));
        }
    }, [disableButtonForSeconds, dispatch]);

    return (
        <Block className={className} dataTestId={dataTestId} notPadded>
            <Route id={formSteps.init} visibleId={currentFormStep}>
                <InitStep
                    accountType={accountType}
                    analyticsWebView={analyticsWebView}
                    birthdaySettings={initStepSettings?.birthdaySettings}
                    fioSettings={initStepSettings?.fioSettings}
                    hasPseudoDraft={initStepSettings?.hasPseudoDraft}
                    phoneSettings={initStepSettings?.phoneSettings}
                    progressBarSettings={initStepSettings?.progressBarSettings}
                    securityWarrantyBlockSettings={initStepSettings?.securityWarrantyBlockSettings}
                    teenagerSettings={teenagerSettings}
                    title={title}
                >
                    {sopdContentSlot}
                </InitStep>
            </Route>
            <Route
                ids={[formSteps.identification_client, formSteps.authentication_client]}
                visibleId={currentFormStep}
            >
                <ShadowBoxDesktop>
                    <SmsVerification />
                </ShadowBoxDesktop>
            </Route>
            <Route id={formSteps.documents_sign} visibleId={currentFormStep}>
                <ShadowBoxDesktop>
                    <DocumentSignSmsVerification />
                </ShadowBoxDesktop>
            </Route>
            <Route id={formSteps.esia_data_addition} visibleId={currentFormStep}>
                <EsiaDataAdditionStep
                    addressSettings={esiaDataAdditionStepSettings?.addressSettings}
                    analyticsWebView={analyticsWebView}
                    description={esiaDataAdditionStepSettings?.description}
                    passportSettings={esiaDataAdditionStepSettings?.passportSettings}
                    progressBarSettings={esiaDataAdditionStepSettings?.progressBarSettings}
                    securityWarrantyBlockSettings={
                        esiaDataAdditionStepSettings?.securityWarrantyBlockSettings
                    }
                    title={title}
                />
            </Route>
            <Route id={formSteps.passport_addition} visibleId={currentFormStep}>
                <PassportAdditionStepDesktop
                    passportSettings={passportAdditionStepSettings?.passportSettings}
                    plateButtonText={passportAdditionStepSettings?.plateButtonText}
                    plateDescription={passportAdditionStepSettings?.plateDescription}
                    plateImageSrc={passportAdditionStepSettings?.plateImageSrc}
                    progressBarSettings={passportAdditionStepSettings?.progressBarSettings}
                    title={title}
                />
            </Route>
            <Route id={formSteps.address_addition} visibleId={currentFormStep}>
                <AddressAdditionStepDesktop
                    addressSettings={addressAdditionStepSettings?.addressSettings}
                    loadingText={addressAdditionStepSettings?.loadingText}
                    loadingTextIconSrc={loadingIconSrc}
                    passportSettings={addressAdditionStepSettings?.passportSettings}
                    progressBarSettings={addressAdditionStepSettings?.progressBarSettings}
                    securityWarrantyBlockSettings={
                        addressAdditionStepSettings?.securityWarrantyBlockSettings
                    }
                    title={title}
                />
            </Route>
            <Route id={formSteps.documents_sign_information} visibleId={currentFormStep}>
                <DocumentsSignInformationStep
                    analyticsWebView={analyticsWebView}
                    primaryButton={documentSignInformationStepSettings?.primaryButton}
                    progressBarSettings={documentSignInformationStepSettings?.progressBarSettings}
                    showDownloadLinkAs={documentSignInformationStepSettings?.showDownloadLinkAs}
                    title={title}
                />
            </Route>
            <Route id={formSteps.documents_sign_information_client} visibleId={currentFormStep}>
                <DocumentsSignInformationClientStep
                    addressLabel={documentSignInformationClientStepSettings?.addressLabel}
                    birthdateLabel={documentSignInformationClientStepSettings?.birthdateLabel}
                    fioLabel={documentSignInformationClientStepSettings?.fioLabel}
                    passportLabel={documentSignInformationClientStepSettings?.passportLabel}
                    primaryButton={documentSignInformationClientStepSettings?.primaryButton}
                    progressBarSettings={
                        documentSignInformationClientStepSettings?.progressBarSettings
                    }
                    secondaryButton={documentSignInformationClientStepSettings?.secondaryButton}
                    showDownloadLinkAs={
                        documentSignInformationClientStepSettings?.showDownloadLinkAs
                    }
                    title={documentSignInformationClientStepSettings?.title}
                />
            </Route>
            <Route id={formSteps.esia_login} visibleId={currentFormStep}>
                <ShadowBoxDesktop>
                    <EsiaLoginStepDesktop
                        description={esiaLoginStepSettings?.description}
                        tabCaption={esiaLoginStepSettings?.tabCaption}
                        tabPrimaryButtonText={esiaLoginStepSettings?.tabPrimaryButtonText}
                        tabPrimaryList={esiaLoginStepSettings?.tabPrimaryList}
                        tabSecondaryButtonText={esiaLoginStepSettings?.tabSecondaryButtonText}
                        tabSecondaryDescription={esiaLoginStepSettings?.tabSecondaryDescription}
                        title={esiaLoginStepSettings?.title}
                    />
                </ShadowBoxDesktop>
            </Route>
            <Route id={formSteps.esia_analysis} visibleId={currentFormStep}>
                <ShadowBoxDesktop title={title}>
                    <EsiaAnalysisStepDesktop description={esiaAnalysisStepSettings?.description} />
                </ShadowBoxDesktop>
            </Route>
            <Route id={formSteps.awaiting_client_inn} visibleId={currentFormStep}>
                <ShadowBoxDesktop title={title}>
                    <AwaitingClientInnStepDesktop
                        loadingIconSrc={loadingIconSrc}
                        loadingText={awaitingClientInnStepSettings?.loadingText}
                        progressBarSettings={awaitingClientInnStepSettings?.progressBarSettings}
                        title={title}
                    />
                </ShadowBoxDesktop>
            </Route>
            <Route id={formSteps.awaiting_fns_inn_identification} visibleId={currentFormStep}>
                <ShadowBoxDesktop title={title}>
                    <AwaitingFnsInnIdentificationStepDesktop
                        loadingIconSrc={loadingIconSrc}
                        loadingText={awaitingFnsInnIdentificationStepSettings?.loadingText}
                        progressBarSettings={
                            awaitingFnsInnIdentificationStepSettings?.progressBarSettings
                        }
                        title={title}
                    />
                </ShadowBoxDesktop>
            </Route>
            <Route ids={[formSteps.error, formSteps.error_bad_gateway]} visibleId={currentFormStep}>
                <ShadowBoxDesktop>
                    <Error
                        buttonText={getPropValue(
                            errorStepSettings?.buttonText,
                            defaultErrorSettings.buttonText
                        )}
                        nextRoute={formSteps.init}
                        subtitle={getPropValue(
                            errorStepSettings?.subtitle,
                            defaultErrorSettings.subtitle
                        )}
                        title={getPropValue(errorStepSettings?.title, defaultErrorSettings.title)}
                    />
                </ShadowBoxDesktop>
            </Route>
            <Route id={formSteps.error_esia_invalid_response} visibleId={currentFormStep}>
                <ShadowBoxDesktop>
                    <Error
                        buttonText={getPropValue(
                            errorStepSettings?.buttonText,
                            defaultErrorEsiaInvalidResponseSettings.buttonText
                        )}
                        nextRoute={formSteps.init}
                        subtitle={getPropValue(
                            errorStepSettings?.subtitle,
                            defaultErrorEsiaInvalidResponseSettings.subtitle
                        )}
                        title={getPropValue(
                            errorStepSettings?.title,
                            defaultErrorEsiaInvalidResponseSettings.title
                        )}
                    />
                </ShadowBoxDesktop>
            </Route>
            <Route id={formSteps.error_overlimit} visibleId={currentFormStep}>
                <ShadowBoxDesktop title={title}>
                    <ErrorOverlimitDesktop
                        subtitle={errorOverlimitStepSettings?.subtitle}
                        title={errorOverlimitStepSettings?.title}
                    />
                </ShadowBoxDesktop>
            </Route>
            <Route id={formSteps.error_overlimit_countdown} visibleId={currentFormStep}>
                <ShadowBoxDesktop title={title}>
                    <ErrorOverlimitCountdownDesktop
                        description={errorOverlimitCountdownStepSettings?.description}
                        subtitle={errorOverlimitCountdownStepSettings?.subtitle}
                        title={title}
                    />
                </ShadowBoxDesktop>
            </Route>
            <Route id={formSteps.error_refusal_open_account} visibleId={currentFormStep}>
                <ShadowBoxDesktop>
                    <Error
                        subtitle={getPropValue(
                            errorRefusalOpenAccountStepSettings?.subtitle,
                            defaultErrorRefusalOpenAccount.subtitle
                        )}
                        title={getPropValue(
                            errorRefusalOpenAccountStepSettings?.title,
                            defaultErrorRefusalOpenAccount.title
                        )}
                    />
                </ShadowBoxDesktop>
            </Route>
            <Route
                ids={[
                    formSteps.error_invite_to_office,
                    formSteps.error_invite_to_office_s01,
                    formSteps.error_invite_to_office_s02,
                    formSteps.error_invite_to_office_s03,
                    formSteps.error_invite_to_office_s04,
                ]}
                visibleId={currentFormStep}
            >
                <ShadowBoxDesktop>
                    <Error
                        iconType="office"
                        subtitle={getPropValue(
                            errorInviteToOfficeStepSettings?.subtitle,
                            defaultErrorInviteToOffice.subtitle
                        )}
                        title={getPropValue(
                            errorInviteToOfficeStepSettings?.title,
                            defaultErrorInviteToOffice.title
                        )}
                    />
                </ShadowBoxDesktop>
            </Route>
            <Route id={formSteps.error_passport_not_valid} visibleId={currentFormStep}>
                <ShadowBoxDesktop>
                    <Error
                        subtitle={getPropValue(
                            errorPassportNotValidStepSettings?.subtitle,
                            defaultErrorPassportNotValid.subtitle
                        )}
                        title={getPropValue(
                            errorPassportNotValidStepSettings?.title,
                            defaultErrorPassportNotValid.title
                        )}
                    />
                </ShadowBoxDesktop>
            </Route>
            <Route id={formSteps.error_authorization_to_esia} visibleId={currentFormStep}>
                <ShadowBoxDesktop>
                    <ErrorAuthorizationToEsiaDesktop
                        buttonText={errorAuthorizationToEsiaStepSettings?.buttonText}
                        description={errorAuthorizationToEsiaStepSettings?.description}
                        title={errorAuthorizationToEsiaStepSettings?.title}
                    />
                </ShadowBoxDesktop>
            </Route>
            <Route id={formSteps.error_authorization_to_cancelled} visibleId={currentFormStep}>
                <ShadowBoxDesktop>
                    <Error
                        buttonText={getPropValue(
                            errorAuthorizationToCancelledStepSettings?.buttonText,
                            defaultErrorAuthorizationToCancelledSettingsDesktop.buttonText
                        )}
                        nextRoute={formSteps.init}
                        subtitle={getPropValue(
                            errorAuthorizationToCancelledStepSettings?.subtitle,
                            defaultErrorAuthorizationToCancelledSettingsDesktop.subtitle
                        )}
                        title={getPropValue(
                            errorAuthorizationToCancelledStepSettings?.title,
                            defaultErrorAuthorizationToCancelledSettingsDesktop.title
                        )}
                    />
                </ShadowBoxDesktop>
            </Route>
            <Route id={formSteps.error_authorization_to_courier_call} visibleId={currentFormStep}>
                <ShadowBoxDesktop>
                    <ErrorAuthorizationToCourierCallDesktop
                        buttonText={errorAuthorizationToCourierCallStepSettings?.buttonText}
                        description={errorAuthorizationToCourierCallStepSettings?.description}
                        title={errorAuthorizationToCourierCallStepSettings?.title}
                    />
                </ShadowBoxDesktop>
            </Route>
            <Route id={formSteps.error_underage} visibleId={currentFormStep}>
                <ShadowBoxDesktop>
                    <Error
                        subtitle={getPropValue(
                            errorUnderageStepSettings?.subtitle,
                            defaultErrorUnderageSettings.subtitle
                        )}
                        title={getPropValue(
                            errorUnderageStepSettings?.title,
                            defaultErrorUnderageSettings.title
                        )}
                    />
                </ShadowBoxDesktop>
            </Route>
            <Route id={formSteps.error_delivery_not_prepared} visibleId={currentFormStep}>
                <ShadowBoxDesktop>
                    <ErrorDeliveryNotPreparedDesktop
                        buttonText={getPropValue(
                            errorDeliveryNotPreparedStepSettings?.buttonText,
                            defaultValuesErrorDeliveryNotPrepared.buttonText
                        )}
                        nextRoute={formSteps.preparing_dc_delivery}
                        subtitle={getPropValue(
                            errorDeliveryNotPreparedStepSettings?.subtitle,
                            defaultValuesErrorDeliveryNotPrepared.subtitle
                        )}
                        title={getPropValue(
                            errorDeliveryNotPreparedStepSettings?.title,
                            defaultValuesErrorDeliveryNotPrepared.title
                        )}
                    />
                </ShadowBoxDesktop>
            </Route>
            <Route id={formSteps.preparing_dc_delivery} visibleId={currentFormStep}>
                <DeliveryPreparingDcDeliveryStepDesktop
                    deliveryAddressLabel={
                        deliveryPreparingDcDeliveryStepSettings?.deliveryAddressLabel
                    }
                    deliveryFlow="default"
                    progressBarSettings={
                        deliveryPreparingDcDeliveryStepSettings?.progressBarSettings
                    }
                    subtitle={deliveryPreparingDcDeliveryStepSettings?.subtitle}
                    title={deliveryPreparingDcDeliveryStepSettings?.title}
                />
            </Route>
            <Route id={formSteps.preparing_dc_delivery_no_dt} visibleId={currentFormStep}>
                <DeliveryPreparingDcDeliveryStepDesktop
                    deliveryAddressLabel={
                        deliveryPreparingDcDeliveryStepSettings?.deliveryAddressLabel
                    }
                    deliveryFlow="no_dt"
                    progressBarSettings={
                        deliveryPreparingDcDeliveryStepSettings?.progressBarSettings
                    }
                    subtitle={deliveryPreparingDcDeliveryStepSettings?.subtitle}
                    title={deliveryPreparingDcDeliveryStepSettings?.title}
                />
            </Route>
            <Route id={formSteps.preparing_dc_delivery_no_inn} visibleId={currentFormStep}>
                <DeliveryPreparingDcDeliveryStepDesktop
                    deliveryAddressLabel={
                        deliveryPreparingDcDeliveryNoInnStepSettings?.deliveryAddressLabel
                    }
                    deliveryFlow="no_inn"
                    progressBarSettings={
                        deliveryPreparingDcDeliveryNoInnStepSettings?.progressBarSettings
                    }
                    subtitle={deliveryPreparingDcDeliveryNoInnStepSettings?.subtitle}
                    title={deliveryPreparingDcDeliveryNoInnStepSettings?.title}
                />
            </Route>
            <Route id={formSteps.book_courier_delivery} visibleId={currentFormStep}>
                <DeliveryBookCourierDeliveryDesktop
                    deliveryAddressLabel={
                        deliveryBookCourierDeliveryStepSettings?.deliveryAddressLabel
                    }
                    deliveryFlow="default"
                    deliverySlotsLabel={deliveryBookCourierDeliveryStepSettings?.deliverySlotsLabel}
                    primaryButtonText={deliveryBookCourierDeliveryStepSettings?.primaryButtonText}
                    progressBarSettings={
                        deliveryBookCourierDeliveryStepSettings?.progressBarSettings
                    }
                    subtitle={deliveryBookCourierDeliveryStepSettings?.subtitle}
                    title={deliveryBookCourierDeliveryStepSettings?.title}
                />
            </Route>
            <Route id={formSteps.book_courier_delivery_no_inn} visibleId={currentFormStep}>
                <DeliveryBookCourierDeliveryDesktop
                    deliveryAddressLabel={
                        deliveryBookCourierDeliveryNoInnStepSettings?.deliveryAddressLabel
                    }
                    deliveryFlow="no_inn"
                    deliverySlotsLabel={
                        deliveryBookCourierDeliveryNoInnStepSettings?.deliverySlotsLabel
                    }
                    primaryButtonText={
                        deliveryBookCourierDeliveryNoInnStepSettings?.primaryButtonText
                    }
                    progressBarSettings={
                        deliveryBookCourierDeliveryNoInnStepSettings?.progressBarSettings
                    }
                    subtitle={deliveryBookCourierDeliveryNoInnStepSettings?.subtitle}
                    title={deliveryBookCourierDeliveryNoInnStepSettings?.title}
                />
            </Route>
            <Route id={formSteps.book_office_delivery} visibleId={currentFormStep}>
                <DeliveryBookOfficeDeliveryDesktop
                    deliveryAddressLabel={
                        deliveryBookOfficeDeliveryStepPublicProps?.deliveryAddressLabel
                    }
                    deliveryFlow="default"
                    primaryButtonText={deliveryBookOfficeDeliveryStepPublicProps?.primaryButtonText}
                    progressBarSettings={
                        deliveryBookOfficeDeliveryStepPublicProps?.progressBarSettings
                    }
                    subtitle={deliveryBookOfficeDeliveryStepPublicProps?.subtitle}
                    title={deliveryBookOfficeDeliveryStepPublicProps?.title}
                />
            </Route>
            <Route id={formSteps.book_office_delivery_no_inn} visibleId={currentFormStep}>
                <DeliveryBookOfficeDeliveryDesktop
                    deliveryAddressLabel={
                        deliveryBookOfficeDeliveryNoInnStepPublicProps?.deliveryAddressLabel
                    }
                    deliveryFlow="no_inn"
                    primaryButtonText={
                        deliveryBookOfficeDeliveryNoInnStepPublicProps?.primaryButtonText
                    }
                    progressBarSettings={
                        deliveryBookOfficeDeliveryNoInnStepPublicProps?.progressBarSettings
                    }
                    subtitle={deliveryBookOfficeDeliveryNoInnStepPublicProps?.subtitle}
                    title={deliveryBookOfficeDeliveryNoInnStepPublicProps?.title}
                />
            </Route>
            <Route id={formSteps.delivery_system_error} visibleId={currentFormStep}>
                <ShadowBoxDesktop>
                    <DeliverySystemErrorStepDesktop
                        button={deliverySystemErrorStepSettings?.button}
                        image={deliverySystemErrorStepSettings?.image}
                        modalButtonText={deliverySystemErrorStepSettings?.modalButtonText}
                        modalSubtitle={deliverySystemErrorStepSettings?.modalSubtitle}
                        modalTitle={deliverySystemErrorStepSettings?.modalTitle}
                        subtitle={deliverySystemErrorStepSettings?.subtitle}
                        title={deliverySystemErrorStepSettings?.title}
                    />
                    {deliverySystemErrorSlot}
                </ShadowBoxDesktop>
            </Route>
            <Route id={formSteps.application_accepted_esia_client} visibleId={currentFormStep}>
                <ShadowBoxDesktop>
                    <DeliveryFinalStepDesktop
                        button={getPropValue(
                            applicationAcceptedEsiaClientStepSettings?.button,
                            defaultDeliveryFinalStepSettingsDesktop.button
                        )}
                        image={applicationAcceptedEsiaClientStepSettings.image}
                        subtitle={getPropValue(
                            applicationAcceptedEsiaClientStepSettings?.subtitle,
                            defaultDeliveryFinalStepSettingsDesktop.subtitle
                        )}
                        title={getPropValue(
                            applicationAcceptedEsiaClientStepSettings?.title,
                            defaultDeliveryFinalStepSettingsDesktop.title
                        )}
                    />
                    {deliveryCommonFinalStepSlot}
                </ShadowBoxDesktop>
            </Route>
            <Route
                id={formSteps.application_accepted_no_dc_no_delivery}
                visibleId={currentFormStep}
            >
                <ShadowBoxDesktop>
                    <DeliveryFinalStepDesktop
                        button={getPropValue(
                            applicationAcceptedNoDcNoDeliveryStepSettings?.button,
                            defaultDeliveryFinalStepSettingsDesktop.button
                        )}
                        image={applicationAcceptedNoDcNoDeliveryStepSettings.image}
                        subtitle={getPropValue(
                            applicationAcceptedNoDcNoDeliveryStepSettings?.subtitle,
                            defaultDeliveryFinalStepSettingsDesktop.subtitle
                        )}
                        title={getPropValue(
                            applicationAcceptedNoDcNoDeliveryStepSettings?.title,
                            defaultDeliveryFinalStepSettingsDesktop.title
                        )}
                    />
                    {deliveryCommonFinalStepSlot}
                </ShadowBoxDesktop>
            </Route>
            <Route
                id={formSteps.application_accepted_is_dc_no_delivery}
                visibleId={currentFormStep}
            >
                <ShadowBoxDesktop>
                    <DeliveryFinalStepDesktop
                        button={getPropValue(
                            applicationAcceptedIsDcNoDeliveryStepSettings?.button,
                            defaultDeliveryFinalStepSettingsDesktop.button
                        )}
                        image={applicationAcceptedIsDcNoDeliveryStepSettings.image}
                        subtitle={getPropValue(
                            applicationAcceptedIsDcNoDeliveryStepSettings?.subtitle,
                            defaultDeliveryFinalStepSettingsDesktop.subtitle
                        )}
                        title={getPropValue(
                            applicationAcceptedIsDcNoDeliveryStepSettings?.title,
                            defaultDeliveryFinalStepSettingsDesktop.title
                        )}
                    />
                    {deliveryCommonFinalStepSlot}
                </ShadowBoxDesktop>
            </Route>
            <Route
                id={formSteps.application_accepted_no_status_dc_delivery}
                visibleId={currentFormStep}
            >
                <ShadowBoxDesktop>
                    <DeliveryFinalStepDesktop
                        button={getPropValue(
                            applicationAcceptedNoStatusDcDeliveryStepSettings?.button,
                            defaultDeliveryFinalStepSettingsDesktop.button
                        )}
                        image={applicationAcceptedNoStatusDcDeliveryStepSettings.image}
                        subtitle={getPropValue(
                            applicationAcceptedNoStatusDcDeliveryStepSettings?.subtitle,
                            defaultDeliveryFinalStepSettingsDesktop.subtitle
                        )}
                        title={getPropValue(
                            applicationAcceptedNoStatusDcDeliveryStepSettings?.title,
                            defaultDeliveryFinalStepSettingsDesktop.title
                        )}
                    />
                    {deliveryCommonFinalStepSlot}
                </ShadowBoxDesktop>
            </Route>
            <Route id={formSteps.courier_delivery_application_approved} visibleId={currentFormStep}>
                <ShadowBoxDesktop>
                    <DeliveryFinalStepDesktop
                        button={getPropValue(
                            courierDeliveryApplicationApprovedStepSettings?.button,
                            defaultDeliveryFinalStepSettingsDesktop.button
                        )}
                        image={courierDeliveryApplicationApprovedStepSettings.image}
                        subtitle={getPropValue(
                            courierDeliveryApplicationApprovedStepSettings?.subtitle,
                            defaultDeliveryFinalStepSettingsDesktop.subtitle
                        )}
                        title={getPropValue(
                            courierDeliveryApplicationApprovedStepSettings?.title,
                            defaultDeliveryFinalStepSettingsDesktop.title
                        )}
                    />
                    {deliveryCourierAlfaCardSlot}
                </ShadowBoxDesktop>
            </Route>
            <Route
                id={formSteps.courier_delivery_application_approved_no_inn}
                visibleId={currentFormStep}
            >
                <ShadowBoxDesktop>
                    <DeliveryFinalStepDesktop
                        button={getPropValue(
                            courierDeliveryApplicationApprovedNoInnStepSettings?.button,
                            defaultValuesCourierDeliveryApplicationApprovedNoInnDesktop.button
                        )}
                        image={courierDeliveryApplicationApprovedNoInnStepSettings.image}
                        subtitle={getPropValue(
                            courierDeliveryApplicationApprovedNoInnStepSettings?.subtitle,
                            defaultValuesCourierDeliveryApplicationApprovedNoInnDesktop.subtitle
                        )}
                        title={getPropValue(
                            courierDeliveryApplicationApprovedNoInnStepSettings?.title,
                            defaultValuesCourierDeliveryApplicationApprovedNoInnDesktop.title
                        )}
                    />
                    {deliveryCourierAlfaCardSlot}
                </ShadowBoxDesktop>
            </Route>
            <Route id={formSteps.office_delivery_application_approved} visibleId={currentFormStep}>
                <ShadowBoxDesktop>
                    <DeliveryFinalStepDesktop
                        button={getPropValue(
                            officeDeliveryApplicationApprovedStepSettings?.button,
                            defaultDeliveryFinalStepSettingsDesktop.button
                        )}
                        image={officeDeliveryApplicationApprovedStepSettings.image}
                        subtitle={getPropValue(
                            officeDeliveryApplicationApprovedStepSettings?.subtitle,
                            defaultDeliveryFinalStepSettingsDesktop.subtitle
                        )}
                        title={getPropValue(
                            officeDeliveryApplicationApprovedStepSettings?.title,
                            defaultDeliveryFinalStepSettingsDesktop.title
                        )}
                    />
                    {deliveryOfficeAlfaCardSlot}
                </ShadowBoxDesktop>
            </Route>
            <Route
                id={formSteps.office_delivery_application_approved_no_inn}
                visibleId={currentFormStep}
            >
                <ShadowBoxDesktop>
                    <DeliveryFinalStepDesktop
                        button={getPropValue(
                            officeDeliveryApplicationApprovedNoInnStepSettings?.button,
                            defaultDeliveryFinalStepSettingsDesktop.button
                        )}
                        image={officeDeliveryApplicationApprovedNoInnStepSettings.image}
                        subtitle={getPropValue(
                            officeDeliveryApplicationApprovedNoInnStepSettings?.subtitle,
                            defaultDeliveryFinalStepSettingsDesktop.subtitle
                        )}
                        title={getPropValue(
                            officeDeliveryApplicationApprovedNoInnStepSettings?.title,
                            defaultDeliveryFinalStepSettingsDesktop.title
                        )}
                    />
                    {deliveryOfficeAlfaCardSlot}
                </ShadowBoxDesktop>
            </Route>
            <Route
                id={formSteps.bro_cancelled_courier_delivery_application_approved}
                visibleId={currentFormStep}
            >
                <ShadowBoxDesktop>
                    <DeliveryFinalStepWithErrorDesktop
                        subtitle={getPropValue(
                            broCancelledCourierDeliveryApplicationApprovedSettings?.subtitle,
                            defaultBroCancelledCourierDeliveryApplicationApprovedDesktop.subtitle
                        )}
                        title={getPropValue(
                            broCancelledCourierDeliveryApplicationApprovedSettings?.title,
                            defaultBroCancelledCourierDeliveryApplicationApprovedDesktop.title
                        )}
                    />
                    {deliveryCourierAlfaCardSlot}
                </ShadowBoxDesktop>
            </Route>
            <Route
                id={formSteps.bro_cancelled_office_delivery_application_approved}
                visibleId={currentFormStep}
            >
                <ShadowBoxDesktop>
                    <DeliveryFinalStepWithErrorDesktop
                        subtitle={getPropValue(
                            broCancelledOfficeDeliveryApplicationApprovedSettings?.subtitle,
                            defaultBroCancelledOfficeDeliveryApplicationApprovedDesktop.subtitle
                        )}
                        title={getPropValue(
                            broCancelledOfficeDeliveryApplicationApprovedSettings?.title,
                            defaultBroCancelledOfficeDeliveryApplicationApprovedDesktop.title
                        )}
                    />
                    {deliveryOfficeAlfaCardSlot}
                </ShadowBoxDesktop>
            </Route>
            <Route ids={[formSteps.error_invite_to_office_r04]} visibleId={currentFormStep}>
                <ShadowBoxDesktop>
                    <DeliveryFinalStepWithErrorDesktop
                        image={errorInviteToOfficeR04R05Settings?.image}
                        subtitle={getPropValue(
                            errorInviteToOfficeR04R05Settings?.subtitle,
                            defaultErrorInviteToOfficeR04R05.subtitle
                        )}
                        title={getPropValue(
                            errorInviteToOfficeR04R05Settings?.title,
                            defaultErrorInviteToOfficeR04R05.title
                        )}
                    />
                </ShadowBoxDesktop>
            </Route>
            {/** Modals TODO должен быть удален и перенесен внутрь другого шага */}
            <Route id={modalSteps.changing_address} visibleId={currentModalStep}>
                <ChangingAddress
                    buttonLink={changingAddressSettings?.buttonLink}
                    buttonText={changingAddressSettings?.buttonText}
                    description={changingAddressSettings?.description}
                    directions={changingAddressSettings?.directions}
                    title={changingAddressSettings?.title}
                />
            </Route>
        </Block>
    );
};

export const Component: FC<TInvestSmevDesktop> = props => (
    <StoreProvider>
        <InvestSmevDesktop {...props} />
    </StoreProvider>
);
